import React, { useState, useEffect, useMemo } from 'react';
import { Search, RefreshCw, Users, UserCheck, Clock, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_BASE_URL = 'https://apisaleeasy.psisat.com';

const CustomerSalesmanLookupComponent = () => {
  const [detailData, setDetailData] = useState([]);   // [{ salesman_code, customer_code, customer_name, last_sale_date }]
  const [salesmanMap, setSalesmanMap] = useState({}); // salesman_code → { salesman_name, branch_code }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [searchText, setSearchText] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [summaryRes, detailRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/report-cache/salesman_customer_summary`),
        fetch(`${API_BASE_URL}/api/report-cache/salesman_customer_summary/detail`),
      ]);

      if (!summaryRes.ok) throw new Error(`Summary HTTP ${summaryRes.status}`);
      if (!detailRes.ok) throw new Error(`Detail HTTP ${detailRes.status}`);

      const summaryJson = await summaryRes.json();
      const detailJson = await detailRes.json();

      if (!summaryJson.success) throw new Error(summaryJson.message || 'Summary API error');
      if (detailJson.not_ready) throw new Error('ข้อมูล detail ยังไม่พร้อม กรุณา Refresh ก่อน');
      if (!detailJson.success) throw new Error(detailJson.message || 'Detail API error');

      // สร้าง map salesman_code → info
      const map = {};
      for (const r of (summaryJson.data || [])) {
        map[r.salesman_code?.trim()] = {
          salesman_name: r.salesman_name || '-',
          branch_code: r.branch_code || '-',
        };
      }
      setSalesmanMap(map);
      setDetailData(detailJson.data || []);
      setUpdatedAt(summaryJson.updated_at);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reverse lookup: customer_code → [{ salesman_code, salesman_name, branch_code, last_sale_date }]
  const customerMap = useMemo(() => {
    const map = {};
    for (const r of detailData) {
      const cCode = r.customer_code?.trim();
      if (!cCode) continue;
      if (!map[cCode]) {
        map[cCode] = {
          customer_code: cCode,
          customer_name: r.customer_name || '-',
          salesmen: [],
        };
      }
      map[cCode].salesmen.push({
        salesman_code: r.salesman_code?.trim(),
        salesman_name: salesmanMap[r.salesman_code?.trim()]?.salesman_name || '-',
        branch_code: salesmanMap[r.salesman_code?.trim()]?.branch_code || '-',
        last_sale_date: r.last_sale_date,
      });
    }
    return map;
  }, [detailData, salesmanMap]);

  const results = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (q.length < 3) return [];
    return Object.values(customerMap).filter(
      c =>
        c.customer_code.toLowerCase().includes(q) ||
        c.customer_name.toLowerCase().includes(q)
    );
  }, [customerMap, searchText]);

  const exportExcel = () => {
    if (results.length === 0) return;
    const rows = [];
    for (const customer of results) {
      const sorted = [...customer.salesmen].sort(
        (a, b) => new Date(b.last_sale_date || 0) - new Date(a.last_sale_date || 0)
      );
      for (const s of sorted) {
        rows.push({
          รหัสลูกค้า: customer.customer_code,
          ชื่อลูกค้า: customer.customer_name,
          รหัสเซลล์: s.salesman_code,
          ชื่อเซลล์: s.salesman_name,
          สาขา: s.branch_code,
          ขายล่าสุด: s.last_sale_date ? formatDate(s.last_sale_date) : '-',
        });
      }
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ผลการค้นหา');
    XLSX.writeFile(wb, `customer_salesman_${searchText.trim()}.xlsx`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('th-TH', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">ค้นหาเซลล์จากลูกค้า</h2>
          <p className="text-gray-500 text-sm mt-1">ค้นรหัสหรือชื่อลูกค้า แล้วดูว่ามีเซลล์ไหนเคยขายให้บ้าง</p>
          {updatedAt && (
            <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              ข้อมูล ณ {new Date(updatedAt).toLocaleString('th-TH')}
            </p>
          )}
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'กำลังโหลด...' : 'Refresh'}
        </button>
      </div>

      {/* Search Box */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="พิมพ์รหัสหรือชื่อลูกค้า..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          disabled={loading}
          className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          เกิดข้อผิดพลาด: {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20 text-gray-500">
          <RefreshCw className="w-6 h-6 animate-spin mr-2" />
          <span>กำลังโหลดข้อมูล...</span>
        </div>
      )}

      {/* Empty search state */}
      {!loading && !error && searchText.trim().length < 3 && (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <Search className="w-12 h-12 mb-3 opacity-30" />
          <p className="text-base">พิมพ์รหัสหรือชื่อลูกค้าในช่องค้นหา</p>
          <p className="text-sm mt-1 opacity-70">
            {searchText.trim().length === 0
              ? 'ระบบจะแสดงรายชื่อเซลล์ที่เคยขายให้ลูกค้ารายนั้น'
              : `พิมพ์อีก ${3 - searchText.trim().length} ตัวอักษร เพื่อเริ่มค้นหา`}
          </p>
        </div>
      )}

      {/* No results */}
      {!loading && !error && searchText.trim().length >= 3 && results.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <Users className="w-10 h-10 mb-3 opacity-30" />
          <p className="text-base">ไม่พบลูกค้าที่ตรงกับ "{searchText}"</p>
        </div>
      )}

      {/* Results */}
      {!loading && results.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">พบ {results.length} ลูกค้าที่ตรงกัน</p>
            <button
              onClick={exportExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Download className="w-4 h-4" />
              Export Excel
            </button>
          </div>
          {results.map(customer => (
            <div key={customer.customer_code} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Customer Header */}
              <div className="bg-purple-50 border-b border-purple-100 px-5 py-3 flex items-center gap-3">
                <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <UserCheck className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded">
                      {customer.customer_code}
                    </span>
                    <span className="font-semibold text-gray-900">{customer.customer_name}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    มีเซลล์ {customer.salesmen.length} คน ที่เคยขายให้ลูกค้ารายนี้
                  </p>
                </div>
              </div>

              {/* Salesmen Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-8">#</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">รหัสเซลล์</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ชื่อเซลล์</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">สาขา</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ขายล่าสุด</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {customer.salesmen
                      .sort((a, b) => new Date(b.last_sale_date || 0) - new Date(a.last_sale_date || 0))
                      .map((s, i) => (
                        <tr key={s.salesman_code} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-2.5 text-gray-400 text-xs">{i + 1}</td>
                          <td className="px-4 py-2.5">
                            <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                              {s.salesman_code}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 font-medium text-gray-900">{s.salesman_name}</td>
                          <td className="px-4 py-2.5 text-gray-500 text-xs">{s.branch_code}</td>
                          <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">
                            {formatDate(s.last_sale_date)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerSalesmanLookupComponent;
